/**
 * Direct-to-MinIO upload path (``arbi upload --s3``).
 *
 * Files are SecretBox-encrypted on the client and PUT directly to MinIO via
 * presigned URLs; arbi-app is never in the byte path. This module hosts the
 * input-expansion helper and the command runner; they are imported from
 * ``upload.ts`` when the ``--s3`` flag is set.
 */

import fs from 'node:fs'
import path from 'node:path'
import { isMessageType, type WsTaskUpdateMessage } from '@arbidocs/client'
import {
  documentsNode,
  documents,
  connectWebSocket,
  getRawWorkspaceKey,
  DOC_TERMINAL_STATUSES,
} from '@arbidocs/sdk'
import { resolveWorkspace } from '../helpers.js'
import { store, getConfig } from '../config.js'
import { success, error, warn, status, dim } from '../output.js'
import { printSummary, type UploadOpts, type UploadSummary } from './upload-shared.js'

/** Count the supported files that would actually be uploaded from a directory. */
function countSupportedFiles(dirPath: string): number {
  let count = 0
  for (const entry of fs.readdirSync(dirPath, { withFileTypes: true })) {
    const full = `${dirPath}/${entry.name}`
    if (entry.isDirectory()) {
      count += countSupportedFiles(full)
    } else if (entry.isFile()) {
      const ext = path.extname(entry.name).toLowerCase()
      if (documents.SUPPORTED_EXTENSIONS.has(ext)) count++
    }
  }
  return count
}

/**
 * Merge an ``UploadBatchResult`` (returned by the zip / dir / archive helpers)
 * into the running summary + doc → filename map used for --watch.
 */
function mergeBatchResult(
  summary: UploadSummary,
  uploadedDocs: Map<string, string>,
  result: documents.UploadBatchResult,
  sourceLabel: string,
  opts: UploadOpts
): void {
  for (const [folder, info] of result.folders) {
    summary.folders += 1
    summary.totalFiles += info.fileCount
    if (!opts.json) {
      success(
        `  ${folder || '<root>'}: ${info.fileCount} file(s) → ${info.doc_ext_ids.length} uploaded`
      )
      if (info.skipped.length > 0) {
        warn(`    Skipped: ${info.skipped.map((s) => `${s.file_name} (${s.reason})`).join(', ')}`)
      }
    }
    for (const docId of info.doc_ext_ids) uploadedDocs.set(docId, sourceLabel)
  }
  summary.uploaded += result.doc_ext_ids.length
  summary.skipped += result.skipped.length
  summary.docIds.push(...result.doc_ext_ids)
  summary.skippedDetails.push(...result.skipped)
}

/**
 * Classify and validate the positional inputs for the direct-upload flow.
 *
 * Unlike the legacy path, every input is handled client-side: directories are
 * walked, zips are extracted in memory, and 7z/rar archives shell out to the
 * system ``7z`` command. Nothing is rejected up front — the caller decides
 * how to upload each entry in ``runDirectUploadMode``.
 */
export type DirectInputKind = 'file' | 'dir' | 'zip' | 'archive'

export interface DirectInput {
  kind: DirectInputKind
  absolutePath: string
  originalPath: string
}

export function classifyDirectInputs(inputPaths: string[]): DirectInput[] {
  const out: DirectInput[] = []
  for (const p of inputPaths) {
    if (!fs.existsSync(p)) {
      error(`Path not found: ${p}`)
      process.exit(1)
    }
    const resolved = path.resolve(p)
    const stat = fs.statSync(resolved)
    if (stat.isDirectory()) {
      out.push({ kind: 'dir', absolutePath: resolved, originalPath: p })
      continue
    }
    const ext = path.extname(resolved).toLowerCase()
    if (ext === '.zip') {
      out.push({ kind: 'zip', absolutePath: resolved, originalPath: p })
      continue
    }
    if (documentsNode.ARCHIVE_EXTENSIONS.has(ext)) {
      out.push({ kind: 'archive', absolutePath: resolved, originalPath: p })
      continue
    }
    if (!documents.SUPPORTED_EXTENSIONS.has(ext)) {
      warn(`Skipping unsupported file: ${p}`)
      continue
    }
    out.push({ kind: 'file', absolutePath: resolved, originalPath: p })
  }
  return out
}

/**
 * Execute the upload via the direct-to-MinIO flow.
 *
 * Each positional input is handled based on its kind:
 *   - single file:  uploaded via ``uploadLocalFilesDirect`` at the workspace
 *                   root (or under ``--folder`` if supplied)
 *   - directory:    walked and uploaded via ``uploadLocalDirectoryDirect``,
 *                   preserving the internal folder structure
 *   - zip:          extracted in memory and uploaded via ``uploadLocalZipDirect``
 *   - 7z / rar:     extracted to a temp dir via the system ``7z`` binary, then
 *                   uploaded via ``uploadLocalArchiveDirect``
 *
 * All extraction happens client-side — arbi-app only ever sees metadata and
 * SecretBox ciphertext via the presigned PUT route.
 */
export async function runDirectUploadMode(inputPaths: string[], opts: UploadOpts): Promise<void> {
  const isInteractive = process.stdout.isTTY === true
  const watchPref = getConfig()?.watch !== false
  const willWatch =
    opts.watch === false ? false : opts.watch === true || (watchPref && isInteractive)

  const inputs = classifyDirectInputs(inputPaths)
  if (inputs.length === 0) {
    warn('No supported files found to upload.')
    return
  }

  // ── Dry-run: report what each input would produce without touching net ──
  if (opts.dryRun) {
    for (const inp of inputs) {
      if (inp.kind === 'file') {
        const folder = opts.folder ?? '<workspace root>'
        console.log(`  ${inp.originalPath} → ${folder}`)
      } else if (inp.kind === 'dir') {
        const count = countSupportedFiles(inp.absolutePath)
        console.log(`  ${inp.originalPath}: directory (${count} supported file(s))`)
      } else if (inp.kind === 'zip') {
        console.log(`  ${inp.originalPath}: zip archive (contents would be extracted + uploaded)`)
      } else {
        console.log(
          `  ${inp.originalPath}: ${path
            .extname(inp.absolutePath)
            .slice(1)} archive (contents would be extracted + uploaded)`
        )
      }
    }
    console.log(`Would upload ${inputs.length} input(s) via --s3.`)
    return
  }

  const { arbi, config, accessToken, workspaceId } = await resolveWorkspace(opts.workspace, {
    skipNotifications: willWatch,
  })

  // Unwrap the raw workspace key once — needed for SecretBox encryption and
  // content hashing. Kept in memory only; never logged or persisted.
  const creds = store.requireCredentials()
  const workspaceKey = await getRawWorkspaceKey(arbi, workspaceId, creds.signingPrivateKeyBase64)

  const summary: UploadSummary = {
    totalFiles: 0,
    uploaded: 0,
    skipped: 0,
    duplicates: 0,
    folders: 0,
    docIds: [],
    skippedDetails: [],
  }
  const uploadedDocs = new Map<string, string>()

  // Progress callbacks shared by the dir / zip / archive helpers.
  const directoryOpts: documentsNode.UploadDirectoryDirectOptions = {
    configExtId: opts.config,
    onBatchStart: (progress) => {
      if (!opts.json) {
        dim(
          `  [${progress.batch}/${progress.totalBatches}] Uploading ${progress.filesInBatch} file(s) from ${progress.folder} via direct-to-MinIO...`
        )
      }
    },
  }

  // Collect plain-file inputs and upload them in one direct call at the end
  // so ``--folder`` applies uniformly and we get a single round-trip.
  const plainFiles: string[] = []

  for (const inp of inputs) {
    if (inp.kind === 'file') {
      plainFiles.push(inp.absolutePath)
      continue
    }

    if (inp.kind === 'dir') {
      const result = await documentsNode.uploadLocalDirectoryDirect(
        arbi,
        workspaceKey,
        inp.absolutePath,
        directoryOpts
      )
      if (result.doc_ext_ids.length === 0 && result.skipped.length === 0) {
        warn(`No supported files found in directory: ${inp.originalPath}`)
        continue
      }
      mergeBatchResult(summary, uploadedDocs, result, inp.originalPath, opts)
      if (!opts.json) {
        success(
          `Uploaded directory: ${inp.originalPath} (${result.doc_ext_ids.length} document(s) total)`
        )
      }
      continue
    }

    if (inp.kind === 'zip') {
      const result = await documentsNode.uploadLocalZipDirect(
        arbi,
        workspaceKey,
        inp.absolutePath,
        directoryOpts
      )
      if (result.doc_ext_ids.length === 0 && result.skipped.length === 0) {
        warn(`No supported files found in zip: ${inp.originalPath}`)
        continue
      }
      mergeBatchResult(summary, uploadedDocs, result, inp.originalPath, opts)
      if (!opts.json) {
        success(
          `Uploaded zip: ${inp.originalPath} (${result.doc_ext_ids.length} document(s) total)`
        )
      }
      continue
    }

    // archive (7z / rar)
    const result = await documentsNode.uploadLocalArchiveDirect(
      arbi,
      workspaceKey,
      inp.absolutePath,
      directoryOpts
    )
    if (result.doc_ext_ids.length === 0 && result.skipped.length === 0) {
      warn(`No supported files found in archive: ${inp.originalPath}`)
      continue
    }
    mergeBatchResult(summary, uploadedDocs, result, inp.originalPath, opts)
    if (!opts.json) {
      success(
        `Uploaded archive: ${inp.originalPath} (${result.doc_ext_ids.length} document(s) total)`
      )
    }
  }

  // Flush any plain-file inputs as a single direct call.
  if (plainFiles.length > 0) {
    if (!opts.json) {
      dim(
        `  Uploading ${plainFiles.length} file(s)${opts.folder ? ` → ${opts.folder}` : ''} via direct-to-MinIO...`
      )
    }
    const result = await documentsNode.uploadLocalFilesDirect(arbi, workspaceKey, plainFiles, {
      folder: opts.folder,
      configExtId: opts.config,
    })
    summary.totalFiles += plainFiles.length
    summary.uploaded += result.doc_ext_ids.length
    summary.skipped += result.skipped.length
    summary.docIds.push(...result.doc_ext_ids)
    summary.skippedDetails.push(...result.skipped)
    for (let i = 0; i < result.doc_ext_ids.length && i < plainFiles.length; i++) {
      uploadedDocs.set(result.doc_ext_ids[i], path.basename(plainFiles[i]))
    }
    if (!opts.json) {
      success(
        `  ${opts.folder || '<root>'}: ${result.doc_ext_ids.length} uploaded, ${result.skipped.length} skipped`
      )
    }
  }

  if (opts.json) {
    console.log(JSON.stringify(summary))
  } else {
    printSummary(summary)
  }

  // Optional watch pass — reuses the same WS flow as the legacy mode.
  if (willWatch && uploadedDocs.size > 0) {
    const pending = new Set(uploadedDocs.keys())
    const failed = new Map<string, string>()
    console.log(`\nWatching ${pending.size} document(s)...`)

    let onDone!: () => void
    const done = new Promise<void>((r) => {
      onDone = r
    })

    const conn = await connectWebSocket({
      baseUrl: config.baseUrl,
      accessToken,
      onMessage: (msg) => {
        if (isMessageType<WsTaskUpdateMessage>(msg, 'task_update')) {
          if (!pending.has(msg.doc_ext_id)) return
          const docName = uploadedDocs.get(msg.doc_ext_id) || msg.file_name
          const extra = msg as Record<string, unknown>
          if (msg.status === 'failed') {
            const reason =
              (extra.error_reason as string) ||
              (extra.status_details as string) ||
              (extra.detail as string) ||
              'Unknown error'
            failed.set(msg.doc_ext_id, reason)
            console.log(`  ${docName}: ${status(msg.status)} — ${reason}`)
            pending.delete(msg.doc_ext_id)
          } else {
            console.log(`  ${docName}: ${status(msg.status)} (${msg.progress}%)`)
            if (DOC_TERMINAL_STATUSES.has(msg.status)) pending.delete(msg.doc_ext_id)
          }
          if (pending.size === 0) conn.close()
        }
      },
      onClose: () => {
        if (failed.size > 0) {
          error(`\n${failed.size} document(s) failed to process:`)
          for (const [docId, reason] of failed) {
            error(`  ${uploadedDocs.get(docId) || docId}: ${reason}`)
          }
        }
        if (pending.size > 0) {
          warn(`\nConnection closed. ${pending.size} document(s) still processing.`)
          dim('Run "arbi watch" to continue monitoring, or "arbi docs" to check status.')
        } else if (failed.size === 0 && uploadedDocs.size > 0) {
          success('\nAll documents processed successfully.')
        }
        onDone()
      },
    })

    await done
  }
}
